import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Action, Movement } from "@arena/shared";
import { BulletView } from "../game/bulletView.ts";
import { CombatSpriteView } from "../game/combatSpriteView.ts";
import { HiddenPistolEcho } from "../game/pistolVisuals.ts";
import { SandalView } from "../game/sandalView.ts";
import { SummonView } from "../game/summonView.ts";
import { AuraView } from "../game/auraView.ts";
import { AspView } from "../game/cleopatraViews.ts";
import { BriocheView } from "../game/antoniettaViews.ts";
import { ANTONIETTA_SPIN, CLEOPATRA_CHARM } from "@arena/character-data";
import type { ChampionFx, ChampionVisuals, DrawnFighter, SlamEvent } from "./types.ts";

export type { ChampionFx, ChampionVisuals, DrawnFighter, MoveEvent, ShotView, SlamEvent } from "./types.ts";

const spot = new Vector3();
const specials = (action: number) => action === Action.Special1 || action === Action.Special2;
const strikes = (action: number) => action === Action.Light1 || action === Action.Light2 || action === Action.Light3;

/** A Super Slam that strikes elsewhere (decoys, tentacles, a transformation): its own banner, its points. */
function ownSlam(fx: ChampionFx, caster: DrawnFighter, event: SlamEvent, banner: string, ms: number, spark: (c: DrawnFighter) => void): boolean {
  if (event.targets.length === 0) {
    spark(caster);
    if (event.caster === fx.localId()) fx.matchHud.flash(banner, ms);
  } else if (event.points > 0) {
    fx.effects.popup(`+${event.points}`, caster.view.chest(spot), "score");
  }
  return true;
}

/** Dante (docs/DANTE_KIT.md): Virgilio and roots where he aims, the dive, pages and slashes. */
const DANTE: ChampionVisuals = {
  id: "dante",
  aimLabel: () => "Virgilio",
  aimDirection: () => "il Malebranche",
  unpredicted: (_state, action) => action === Action.Special1,
  shotView: (fx, shot) => (fx.look2d && !shot.prop ? new SummonView(fx.scene, "malebranche") : null),
  slamPulse: 1,
  onMove(fx, _caster, event) {
    fx.propsView?.disturb(event.x, event.z, event.action === Action.AirAttack ? 3 : 2);
    fx.hd2d?.pulse(event.action === Action.AirAttack ? 0.3 : 0.12);
    if (event.action === Action.Special1) {
      fx.summon(new SummonView(fx.scene, "virgilio"), event);
      fx.moveEffects.ground("dante-roots", event.x, event.y, event.z, 1.5);
    } else if (event.action === Action.Special2) {
      fx.moveEffects.ground("dante-inferno-v2", event.x + event.fx, event.y, event.z + event.fz, 0.7);
    } else if (event.action === Action.AirAttack) {
      if (event.caster === fx.localId()) fx.rig.shake(0.16);
      fx.moveEffects.ground("dante-pages", event.x, event.y, event.z, 1.8);
      fx.effects.shockwave(new Vector3(event.x, event.y, event.z), 1.8);
    } else if (event.action === Action.Light2 || event.action === Action.Light3 || event.action === Action.DashAttack) {
      if (event.action === Action.Light3) {
        fx.flourish(new CombatSpriteView(fx.scene, "tome"), event.x + event.fx * 1.15, event.y + 0.7, event.z + event.fz * 1.15, event.fx, event.fz);
      }
      fx.moveEffects.ground("dante-slash", event.x + event.fx, event.y, event.z + event.fz, 1.5);
    }
  },
};

/** Gandhi (docs/GANDHI_REFACTOR.md): the staff, the sandal boomerang, the salt, the decoys. */
const GANDHI: ChampionVisuals = {
  id: "gandhi",
  noSlamWindup: true,
  shotView(fx, shot) {
    if (shot.kind === "sandal") return new SandalView(fx.scene);
    return fx.look2d && !shot.prop ? new CombatSpriteView(fx.scene, "salt") : null;
  },
  onMove(fx, _caster, event) {
    // S1: the animated throw and the real spinning sandal carry this move.
    if (event.action === Action.DashAttack) {
      fx.moveEffects.ground("gandhi-staff", event.x + event.fx * 0.8, event.y, event.z + event.fz * 0.8, 1.8);
      fx.propsView?.disturb(event.x, event.z, 2);
      if (event.caster === fx.localId()) fx.rig.shake(0.08);
    } else if (strikes(event.action)) {
      fx.moveEffects.ground("gandhi-staff", event.x + event.fx * 0.7, event.y, event.z + event.fz * 0.7, event.action === Action.Light1 ? 1 : 1.4);
    }
  },
  onSlam: (fx, caster, event) => ownSlam(fx, caster, event, "UNO, NESSUNO E GANDHI!", 1000, (c) => fx.effects.spark(c.view.chest(spot), false)),
};

/** Mussolini: the pistol, the photographers, the barricade, the air raid. */
const MUSSOLINI: ChampionVisuals = {
  id: "mussolini",
  noSlamArea: true,
  unpredicted: (_state, action) => specials(action),
  // His own shots are already drawn by the pistol in his hand.
  shotView: (fx, shot) => (fx.look2d && !shot.prop ? (fx.isLocal(shot.owner) ? new HiddenPistolEcho() : new BulletView(fx.scene)) : null),
  // Both gun clips already contain their flash on the firing frame: a late event must not add another.
  onMove: () => {},
};

/** Kafka (docs/KAFKA_VIRGILIO_REFACTOR.md): the bucket of water, the infestation, the beetle. */
const KAFKA: ChampionVisuals = {
  id: "kafka",
  aimLabel: (state) => (state.metamorphTicks === 0 ? "l’infestazione" : null),
  unpredicted: (state, action) => state.metamorphTicks === 0 && specials(action),
  onMove(fx, caster, event) {
    if (caster.state.metamorphTicks === 0) {
      if (!specials(event.action)) fx.kafkaWater.splash(event.x, event.y, event.z, event.fx, event.fz, 2.1);
    } else if (event.caster === fx.localId()) {
      fx.rig.shake(0.12);
    }
  },
  onSlam: (fx, caster, event) =>
    ownSlam(fx, caster, event, "METAMORFOSI · 20 SECONDI", 1400, (c) => {
      fx.effects.spark(c.view.chest(spot), true);
      fx.hd2d?.pulse(0.6);
      if (event.caster === fx.localId()) fx.rig.shake(0.3);
    }),
};

/** H. P. Lovecraft (docs/personaggi/lovecraft.md): servants and tentacles are drawn from the state. */
const LOVECRAFT: ChampionVisuals = {
  id: "lovecraft",
  unpredicted: (_state, action) => specials(action),
  onSlam(fx, caster, event) {
    if (event.targets.length === 0) {
      if (event.caster === fx.localId()) fx.matchHud.flash("IL RISVEGLIO DEGLI ABISSI", 1300);
      return true;
    }
    // A tentacle that struck: the target lit, the point to Lovecraft.
    for (const id of event.targets) {
      const target = fx.fighter(id);
      if (target) fx.effects.spark(target.view.chest(spot), true);
    }
    if (event.points > 0) fx.effects.popup(`+${event.points}`, caster.view.chest(spot), "score");
    return true;
  },
};

/** Kings at maximum aura: from their Super Slam until the aura runs out. */
const maxAura = new Set<string>();
const auras = new Map<string, AuraView>();

/** Auramennone (docs/personaggi/auramennone.md): the phalanx is drawn from the state, the aura around him. */
const AURAMENNONE: ChampionVisuals = {
  id: "auramennone",
  noSlamArea: true,
  unpredicted: (_state, action) => specials(action),
  onMove(fx, caster, event) {
    if (event.action === Action.Special2) {
      // The pose was not interrupted: the reward, for everyone to see.
      fx.effects.popup("+AURA", caster.view.chest(spot), "score");
      fx.hd2d?.pulse(0.35);
    } else if (event.action === Action.AirAttack) {
      fx.effects.shockwave(new Vector3(event.x, event.y, event.z), 1.8);
      fx.propsView?.disturb(event.x, event.z, 2);
      if (event.caster === fx.localId()) fx.rig.shake(0.14);
    } else if (event.action === Action.DashAttack) {
      fx.propsView?.disturb(event.x, event.z, 2);
    }
  },
  frame(fx, fighters, dt) {
    const present = new Set<string>();
    for (const [id, f] of fighters) {
      present.add(id);
      const active = f.state.metamorphTicks > 0 && !f.state.knockedOut;
      if (!active) maxAura.delete(id);
      let view = auras.get(id);
      if (!view && !active) continue;
      if (!view) auras.set(id, (view = new AuraView(fx.scene)));
      view.update(f.view.position, active, maxAura.has(id), dt, fx.scene.activeCamera?.position);
    }
    for (const [id, view] of auras) {
      if (present.has(id)) continue;
      view.dispose();
      auras.delete(id);
      maxAura.delete(id);
    }
  },
  onSlam: (fx, caster, event) =>
    ownSlam(fx, caster, event, "AURA MASSIMA · 6 SECONDI", 1400, (c) => {
      maxAura.add(event.caster);
      fx.effects.spark(c.view.chest(spot), true);
      fx.effects.shockwave(c.view.position.clone(), 3.5);
      fx.hd2d?.pulse(0.8);
      if (event.caster === fx.localId()) fx.rig.shake(0.3);
    }),
};

/**
 * Cleopatra (docs/personaggi/cleopatra.md), femme fatale: the asp slithering on
 * the floor, a blown kiss for the nose; the bath and the marks over charmed and
 * poisoned fighters are drawn from the state.
 */
const CLEOPATRA: ChampionVisuals = {
  id: "cleopatra",
  unpredicted: (_state, action) => specials(action),
  shotView: (fx, shot) => (fx.look2d && !shot.prop ? new AspView(fx.scene) : null),
  // The nose: a blown kiss, a ring as wide as the charm; the hearts follow on whoever it caught.
  onSlam(fx, caster, event) {
    fx.effects.shockwave(caster.view.position.clone(), CLEOPATRA_CHARM.radius);
    fx.effects.popup("MUAH!", caster.view.chest(spot).addInPlaceFromFloats(0, 0.9, 0), "combo");
    fx.hd2d?.pulse(0.5);
    if (event.caster === fx.localId()) {
      fx.matchHud.flash(event.targets.length > 0 ? `PERDONO LA TESTA PER TE${event.points > 0 ? ` +${event.points}` : ""}` : "Nessuno da sedurre… per ora", 1300);
      fx.rig.shake(0.2);
    } else if (event.points > 0) {
      fx.effects.popup(`+${event.points}`, caster.view.chest(spot), "score");
    }
    return true;
  },
};

/**
 * Maria Antonietta (docs/personaggi/antonietta.md), rococo diva: the brioche
 * tossed in an arc; the mines, their blasts and the wig are drawn from the
 * state, the whirlwind by her sprite while `spinTicks` lasts.
 */
const ANTONIETTA: ChampionVisuals = {
  id: "antonietta",
  unpredicted: (_state, action) => specials(action),
  shotView: (fx, shot) => (fx.look2d && !shot.prop ? new BriocheView(fx.scene) : null),
  // The hairpin comes out: a gust as wide as the whirlwind; then each enemy it scores shows its points.
  onSlam(fx, caster, event) {
    if (event.targets.length > 0) {
      if (event.points > 0) fx.effects.popup(`+${event.points}`, caster.view.chest(spot), "score");
      return true;
    }
    fx.effects.shockwave(caster.view.position.clone(), ANTONIETTA_SPIN.radius + Movement.radius);
    fx.effects.popup("RIVOLUZIONE!", caster.view.chest(spot).addInPlaceFromFloats(0, 1.2, 0), "combo");
    fx.hd2d?.pulse(0.5);
    if (event.caster === fx.localId()) {
      fx.matchHud.flash("VIVA LA RIVOLUZIONE! (LA MIA)", 1300);
      fx.rig.shake(0.2);
    }
    return true;
  },
};

/**
 * Giovanna d'Arco (docs/personaggi/giovanna.md): the banner is drawn from the
 * state; the Super Slam calls her white warhorse, and each enemy her mounted
 * blows score shows its points.
 */
const GIOVANNA: ChampionVisuals = {
  id: "giovanna",
  unpredicted: (_state, action) => action === Action.Special1,
  onSlam(fx, caster, event) {
    if (event.targets.length > 0) {
      if (event.points > 0) fx.effects.popup(`+${event.points}`, caster.view.chest(spot), "score");
      return true;
    }
    fx.effects.shockwave(caster.view.position.clone(), 2.5);
    fx.effects.popup("PER LA FRANCIA!", caster.view.chest(spot).addInPlaceFromFloats(0, 1.2, 0), "combo");
    fx.hd2d?.pulse(0.5);
    if (event.caster === fx.localId()) {
      fx.matchHud.flash("A CAVALLO! ME L'HANNO DETTO LE VOCI", 1300);
      fx.rig.shake(0.2);
    }
    return true;
  },
};

const VISUALS = new Map([DANTE, GANDHI, MUSSOLINI, KAFKA, LOVECRAFT, AURAMENNONE, CLEOPATRA, ANTONIETTA, GIOVANNA].map((v) => [v.id, v]));

/** Every champion's visuals, for the per-frame hooks. */
export function allVisuals(): Iterable<ChampionVisuals> {
  return VISUALS.values();
}

/** The visuals of a champion, if it has any of its own. */
export function visualsOf(championId: string): ChampionVisuals | undefined {
  return VISUALS.get(championId);
}
